import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Auth } from '@common/auth/models/auth.model';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { User } from '@common/users/models/user.model';
import { AddPasswordRequest } from '@portal-core/auth/models/add-password-request.model';
import { AuthLicenseUserData } from '@portal-core/auth/models/auth-license-user-data.model';
import { CentralInstance } from '@portal-core/auth/models/central-instance.model';
import { CompletedInvite } from '@portal-core/auth/models/completed-invite.model';
import { HandoffToken } from '@portal-core/auth/models/handoff-token.model';
import { SubdomainLicense } from '@portal-core/auth/models/subdomain-license.model';
import { VerifiedUser } from '@portal-core/auth/models/verified-user.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthInterceptorHttpParams } from '@portal-core/auth/util/auth-interceptor-http-params';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

@Service()
export class AuthApiService {
  protected readonly http = inject(HttpClient);
  protected readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getUser$(ignoreAuthErrors: boolean = false): Observable<User> {
    return this.http.get<User>(`${this.apiService.centralApiBaseUri}/api/users/self`, {
      params: new AuthInterceptorHttpParams({
        ignoreAuthErrors
      })
    });
  }

  @SharePendingRequest()
  getLicenseUserData$(ignoreAuthErrors: boolean = false): Observable<AuthLicenseUserData> {
    return this.http.get<AuthLicenseUserData>(`${this.apiService.centralApiBaseUri}/api/Users/MyLicenseData`, {
      params: new AuthInterceptorHttpParams({
        ignoreAuthErrors
      })
    });
  }

  @SharePendingRequest()
  login$(email: string, password: string): Observable<Auth> {
    const body = new URLSearchParams();
    body.set('client_id', environment.centralApiClientId);
    body.set('grant_type', 'password');
    body.set('username', email);
    body.set('password', password);

    return this.http.post<Auth>(`${this.apiService.centralApiBaseUri}/token`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      params: new AuthInterceptorHttpParams({
        ignoreRefreshToken: true
      })
    });
  }

  @SharePendingRequest()
  refreshToken$(): Observable<void> {
    // The server reads the refresh token from its HttpOnly cookie and takes the client from the token's session
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');

    // The refresh endpoint sets the new access/refresh tokens in http-only cookies and returns no body.
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/token/refresh`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      params: new AuthInterceptorHttpParams({
        ignoreRefreshToken: true
      })
    });
  }

  logout$(returnUrl: string): Observable<string> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/users/logout${returnUrl ? '?returnUrl=' + encodeURIComponent(returnUrl) : ''}`, {}, {
      responseType: 'text',
      withCredentials: true
    });
  }

  loginToLicense$(): Observable<Auth> {
    return this.http.post<Auth>(`${this.apiService.centralApiBaseUri}/api/licenses/login`, { withCredentials: true });
  }

  requestHandoffToken$(licenseId: number): Observable<HandoffToken> {
    return this.http.post<HandoffToken>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/token/handoff?clientId=${encodeURIComponent(environment.centralApiClientId)}`, {
      withCredentials: true
    });
  }

  redeemHandoffToken$(token: string): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/token/handoff/redeem`, {
      ClientId: environment.centralApiClientId,
      Token: token
    });
  }

  /**
   * Requests a handoff token that moves the session to the portal host of the license the user is
   * already signed in to. Unlike requestHandoffToken$ this never crosses a license boundary, so it
   * works for SSO licenses too.
   */
  requestRelocationHandoffToken$(): Observable<HandoffToken> {
    return this.http.post<HandoffToken>(`${this.apiService.centralApiBaseUri}/api/licenses/token/handoff/relocate`, {}, {
      // A caller who is not signed in gets a 401 here, which is an answer rather than an error:
      // it means the login form should be shown instead.
      params: new AuthInterceptorHttpParams({ ignoreAuthErrors: true }, { clientId: environment.centralApiClientId })
    });
  }

  /**
   * Redeems a relocation handoff token against the authorization server, so the session cookies are
   * set on the host it answers on.
   *
   * This goes to the authorization server rather than the API because only it can see which host it
   * was called on: on the portal host the API sits behind a proxy that replaces the Host header, so
   * it can resolve neither the license nor the token's authority.
   */
  redeemRelocationToken$(token: string): Observable<any> {
    return this.http.post(`${this.apiService.centralAuthBaseUri}/connect/session`, {
      ClientId: environment.centralApiClientId,
      Token: token
    }, {
      params: new AuthInterceptorHttpParams({ ignoreAuthErrors: true, ignoreRefreshToken: true })
    });
  }

  addPassword$(addPasswordRequest: AddPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/users/AddPassword`, addPasswordRequest);
  }

  changeExpiredPassword$(userName: string, currentPassword: string, newPassword: string): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/users/ChangeExpiredPassword`, {
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
      UserName: userName
    });
  }

  @SharePendingRequest()
  completeInvite$(userId: string): Observable<CompletedInvite> {
    return this.http.get<CompletedInvite>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/UserToCompleteRegistration`);
  }

  requestPasswordReset$(email: string, client?: string): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/users/ForgotPassword`, null, { params: { email, client } });
  }

  /**
   * Requests access to a site for the signed-in user.
   *
   * This base implementation is dead and its URL matches no route. The real endpoint is
   * `POST /api/users/{siteKey}/RequestSiteAccess` - the site key is not optional, since the server
   * resolves the site from it and 404s without one. Only AuthSiteApiService can supply it, and
   * portal-output swaps that in for AuthApiService, so every app that can actually reach this
   * feature gets the override. Nothing reaches the body below: its sole caller is
   * AuthService.requestSiteAccess$, called only from the site-access-denied alert card, which is
   * mounted only in portal-output. Portal-client renders the preview variant of that card, whose
   * click handler is deliberately empty.
   *
   * It stays declared because AuthService injects AuthApiService by type, so the base has to carry
   * a signature for that call to compile. Returns void because the real endpoint replies Ok() with
   * an empty body.
   */
  requestSiteAccess$(): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/users/RequestSiteAccess`, null);
  }

  requestLicenseAccess$(): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/users/RequestLicenseAccess`, null);
  }

  samlSsoLogin$(returnUrl: string, clientDomain: string): Observable<string> {
    const body = new URLSearchParams();
    body.set('client_id', environment.centralApiClientId);
    body.set('return_url', returnUrl ? returnUrl : '');
    body.set('client_domain', clientDomain);

    return this.http.post(`${this.apiService.centralApiBaseUri}/api/users/SamlLoginRequest`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      responseType: 'text'
    });
  }

  resetPassword$(userId: string, token: string, password: string): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/users/ResetPassword`, {
      NewPassword: password,
      Token: token,
      UserId: userId
    });
  }

  updatePassword$(userId: string, currentPassword: string, newPassword: string): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/ChangePassword`, {
      CurrentPassword: currentPassword,
      NewPassword: newPassword
    });
  }

  verifyUserToken$(userId: string, token: string, confirmEmail: boolean): Observable<VerifiedUser> {
    return this.http.post<VerifiedUser>(`${this.apiService.centralApiBaseUri}/api/users/VerifyUserToken`, {
      ConfirmEmail: confirmEmail,
      Token: token,
      UserId: userId
    });
  }

  verifySsoUserToken$(userId: string, token: string, teamName?: string): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/ConfirmUserTokenAndCreate${teamName ? '?teamName=' + encodeURIComponent(teamName) : ''}`, JSON.stringify(token),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      });
  }

  @SharePendingRequest()
  getMinPasswordLength$(userName: string): Observable<number> {
    return this.http.get<number>(`${this.apiService.centralApiBaseUri}/api/users/${userName}/GetMinPasswordLength`);
  }

  @SharePendingRequest()
  getCentralInstances$(): Observable<CentralInstance[]> {
    return this.http.get<CentralInstance[]>(`${this.apiService.centralGlobalApiBaseUri}/api/common/instances`);
  }

  @SharePendingRequest()
  getLicenseCentralInstance$(licenseVanityUrl: string): Observable<CentralInstance> {
    return this.http.get<CentralInstance>(`${this.apiService.centralGlobalApiBaseUri}/api/licenses/${licenseVanityUrl}/instance`);
  }

  @SharePendingRequest()
  getCentralInstanceByInstanceCode$(instanceCode: string): Observable<CentralInstance> {
    return this.http.get<CentralInstance>(`${this.apiService.centralGlobalApiBaseUri}/api/common/instance/${instanceCode}`);
  }

  @SharePendingRequest()
  getIsVanityUrlPresent$(vanity: string, checkAllInstances: boolean): Observable<MadCloudResult<void>> {
    return this.http.post<MadCloudResult<void>>(`${this.apiService.centralGlobalApiBaseUri}/api/licenses/IsVanityUrlPresent?checkAllInstances=${checkAllInstances}`, { VanityBaseUrl: vanity });
  }

  @SharePendingRequest()
  getLicenseByDomain$(): Observable<SubdomainLicense> {
    return this.http.get<SubdomainLicense>(`${this.apiService.centralApiBaseUri}/api/licenses/getDomainLicense`);
  }
}
